import React, { useEffect, useState } from "react";
import { useParams, Routes, Route, NavLink, useLocation } from "react-router-dom";
import {
  doc,
  getDoc,
  collection,
  query,
  where,
  getDocs,
} from "firebase/firestore";
import { db, auth } from "../../firebase";
import { useAuthState } from "react-firebase-hooks/auth";

// Import sub-pages
import RosterPage from "./RosterPage";
import SchedulePage from "./SchedulePage";
import GameStatsPage from "./GameStatsPage";
import PlayerStatsPage from "./PlayerStatsPage";
import ViewGamesPage from "./ViewGamesPage";
import ViewStatsPage from "./ViewStatsPage";
import PlayerStatsLeaderPage from "./PlayerStatsLeaderPage";
import PlayerViewPage from "./PlayerViewPage";
import AnnouncementsPage from "./AnnouncementsPage";
import AddAnnouncementsPage from "./AddAnnouncementsPage";
import RosterAddPage from "./RosterAddPage";
import MediaPage from "./MediaPage";
import TeamAvailability from "./TeamAvailability";

const ManagerRoute = ({ isManager, children }) => {
  if (!isManager) return <p style={{ textAlign: "center", padding: "0.5rem", color: "#e53e3e" }}>Not authorized</p>;
  return children;
};

function TeamDashboard() {
  const { teamId } = useParams();
  const [user, loadingUser] = useAuthState(auth);
  const [teamData, setTeamData] = useState(null);
  const [isManager, setIsManager] = useState(false);
  const [isMember, setIsMember] = useState(false);
  const [rosterPlayerId, setRosterPlayerId] = useState(null);
  const location = useLocation();

  // Styles with improved tap/click targets
  const styles = {
    container: {
      maxWidth: "1200px",
      margin: "0.75rem auto",
      padding: "0.75rem",
      backgroundColor: "#fff",
      borderRadius: "8px",
      boxShadow: "0 2px 8px rgba(0, 0, 0, 0.08)",
      fontFamily: "'Roboto', sans-serif",
    },
    header: {
      display: "flex",
      justifyContent: "center",
      alignItems: "center",
      marginBottom: "0.75rem",
      paddingBottom: "0.5rem",
      borderBottom: "1px solid #e2e8f0",
    },
    heading: {
      color: "#1a365d",
      fontSize: "1.35rem",
      fontWeight: "600",
    },
    nav: {
      backgroundColor: "#f0f4f8",
      padding: "0.5rem",
      borderRadius: "6px",
      marginBottom: "0.75rem",
      display: "flex",
      overflowX: "auto",
      gap: "0.35rem",
      whiteSpace: "nowrap",
      scrollbarWidth: "thin",
    },
    link: {
      color: "#1a365d",
      textDecoration: "none",
      padding: "0.4rem 0.75rem", // Increased padding for better tap targets
      borderRadius: "6px",
      fontSize: "0.9rem", // Slightly larger text
      transition: "background 0.2s",
      fontWeight: "500",
    },
    activeLink: {
      backgroundColor: "#3182ce",
      color: "white",
      fontWeight: "600",
    },
    content: {
      backgroundColor: "#ffffff",
      borderRadius: "6px",
      padding: "0.85rem",
      border: "1px solid #e2e8f0",
    },
    loadingMessage: {
      textAlign: "center",
      padding: "1rem",
      color: "#4a5568",
    },
  };

  useEffect(() => {
    const checkManager = async () => {
      if (!user) return;
      try {
        const teamRef = doc(db, "teams", teamId);
        const teamSnap = await getDoc(teamRef);
        if (teamSnap.exists()) {
          const data = teamSnap.data();
          setTeamData(data);
          setIsManager(data.coachId === user.uid);
        }
      } catch (err) {
        console.error("Error loading team data:", err);
        setIsManager(false);
      }
    };
    checkManager();
  }, [user, teamId]);

  useEffect(() => {
    const checkMembership = async () => {
      if (!user) return;
      
      console.log("Checking membership for user:", user.uid, "email:", user.email, "team:", teamId);
      
      try {
        // First try to verify if user is a team admin or member directly from the team document
        // This avoids the need for roster queries if the user is already in the team members list
        const teamRef = doc(db, "teams", teamId);
        const teamSnap = await getDoc(teamRef);
        
        if (teamSnap.exists()) {
          const data = teamSnap.data();
          setTeamData(data);
          
          // Check if user is the coach or in the members list
          if (data.coachId === user.uid || (data.members && data.members.includes(user.uid))) {
            console.log("User is a direct team member or coach");
            setIsManager(data.coachId === user.uid);
            setIsMember(true);
            
            // Still try to get the player ID for profile access, but don't block on it
            try {
              const playersRef = collection(db, "players");
              const playersQuery = query(playersRef, where("email", "==", user.email));
              const playersSnap = await getDocs(playersQuery);
              
              if (!playersSnap.empty) {
                const playerDoc = playersSnap.docs[0];
                setRosterPlayerId(playerDoc.id);
              }
            } catch (err) {
              console.log("Could not retrieve player document, but user still has access:", err);
            }
            
            return;
          }
        }
        
        // If we're here, user wasn't found in team document directly
        // Try the roster-based approach as a fallback
        console.log("Checking roster-based membership");
        
        // Find player document by email
        const playersRef = collection(db, "players");
        const playersQuery = query(playersRef, where("email", "==", user.email));
        const playersSnap = await getDocs(playersQuery);
        
        if (playersSnap.empty) {
          console.log("No player found with email:", user.email);
          setIsMember(false);
          return;
        }
        
        const playerDoc = playersSnap.docs[0];
        const playerId = playerDoc.id;
        console.log("Found player with ID:", playerId);
        setRosterPlayerId(playerId);
        
        // Now check roster membership
        const rostersRef = collection(db, "rosters");
        const rosterQuery = query(
          rostersRef,
          where("teamId", "==", teamId),
          where("playerId", "==", playerId),
          where("status", "==", "active")
        );
        
        const rosterSnap = await getDocs(rosterQuery);
        const hasRosterEntry = !rosterSnap.empty;
        
        console.log("Roster membership:", hasRosterEntry);
        setIsMember(hasRosterEntry);
        
      } catch (err) {
        console.error("Error during membership check:", err);
        
        if (err.code === 'permission-denied') {
          console.error("Permission denied. This may indicate the security rules need updating.");
        }
        
        // Default to false for safety
        setIsMember(false);
      }
    };
    
    checkMembership();
  }, [user, teamId]);

  if (loadingUser) {
    return <div style={styles.loadingMessage}>Loading user...</div>;
  }

  if (!user) {
    return <div style={styles.loadingMessage}>Please log in to view this page.</div>;
  }

  if (!isManager && !isMember) {
    return <div style={styles.loadingMessage}>You are not a member of this team.</div>;
  }

  const nonManagerLinks = [
    { path: "announcements", label: "Announcements" },
    { path: "roster", label: "Roster" },
    { path: "schedule", label: "Schedule" },
    { path: "view-games", label: "Games" },
    { path: "leaders", label: "Leaders" },
    { path: "media", label: "Media" },
    { path: rosterPlayerId ? `player/${rosterPlayerId}` : "", label: "Profile" },
  ];

  const managerLinks = [
    { path: "announcements", label: "Announcements" },
    { path: "roster", label: "Roster" },
    { path: "schedule", label: "Schedule" },
    { path: "view-games", label: "Games" },
    { path: "leaders", label: "Leaders" },
    { path: "game-stats", label: "Add Game Stats" },
    { path: "player-stats", label: "Add Player Stats" },
    { path: "announcements/add", label: "Add Announcement" },
    { path: "media", label: "Media" },
    { path: rosterPlayerId ? `player/${rosterPlayerId}` : "", label: "Profile" },
    { path: "availability", label: "Availability" },
  ];

  const linksToRender = isManager ? managerLinks : nonManagerLinks;
  const canUpload = isManager;

  return (
    <div style={styles.container}>
      <div style={styles.header}>
        <h2 style={styles.heading}>
          {teamData?.name ? `${teamData.name}` : "Team"} Dashboard
        </h2>
      </div>

      <div style={styles.nav}>
        {linksToRender.map((link) => {
          if (!link.path) return null;
          const to = `/team/${teamId}/dashboard/${link.path}`;
          return (
            <NavLink
              key={link.path}
              to={to}
              end
              style={({ isActive }) =>
                isActive ? { ...styles.link, ...styles.activeLink } : styles.link
              }
            >
              {link.label}
            </NavLink>
          );
        })}
      </div>

      <div style={styles.content}>
        <Routes>
          <Route path="announcements" element={<AnnouncementsPage />} />
          <Route path="roster" element={<RosterPage />} />
          <Route path="roster/add" element={<RosterAddPage />} />
          <Route path="schedule" element={<SchedulePage />} />
          <Route path="view-games" element={<ViewGamesPage />} />
          <Route path="leaders" element={<PlayerStatsLeaderPage />} />
          <Route path="availability" element={<TeamAvailability />} />
          <Route path="player/:playerId" element={<PlayerViewPage />} />
          <Route
            path="game-stats"
            element={
              <ManagerRoute isManager={isManager}>
                <GameStatsPage />
              </ManagerRoute>
            }
          />
          <Route
            path="player-stats"
            element={
              <ManagerRoute isManager={isManager}>
                <PlayerStatsPage />
              </ManagerRoute>
            }
          />
          <Route
            path="announcements/add"
            element={
              <ManagerRoute isManager={isManager}>
                <AddAnnouncementsPage />
              </ManagerRoute>
            }
          />
          <Route
            path="media"
            element={<MediaPage teamId={teamId} canUpload={canUpload} />}
          />
          <Route path="view-stats/:gameId" element={<ViewStatsPage />} />
        </Routes>
      </div>
    </div>
  );
}

export default TeamDashboard;